import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from "react-router-dom";
import { Card, CardBody, Alert, Row, Col, Spinner } from "reactstrap";
import { Line } from "react-chartjs-2";
import {
	faChevronLeft,
	faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactPaginate from "react-paginate";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { format, subDays, addDays, isBefore, isEqual } from "date-fns";
import DateRangePickerComponent from "../../../components/DaterangePicker";
import { PostData } from '../../../services/FetchData';
import { ProductContext } from '../../../store';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import DetailSkeleton from '../../../components/Skeleton/DetailSkeleton';
import CredentialSkeleton from "../../../components/Skeleton/CredentialSkeleton";
import "./Dashboard.scss";

/** Short label for long URLs in the link activity table. */
function formatLinkLabel(url) {
	if (!url) return "—";
	try {
		const parsed = new URL(url);
		const label = `${parsed.hostname}${parsed.pathname}${parsed.search}`;
		return label.length > 72 ? `${label.slice(0, 69)}…` : label;
	} catch {
		return url.length > 72 ? `${url.slice(0, 69)}…` : url;
	}
}

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
);

/** Map API daily series (array of { date, count }) to full date-range labels. */
function mapDailyToDateRange(fullDateKeys, apiSeries) {
	if (!apiSeries?.length) return fullDateKeys.map(() => 0);
	const apiLabels = apiSeries.map((d) => (d.date || "").toString().slice(0, 10));
	const apiValues = apiSeries.map((d) => Number(d.count) || 0);
	return fullDateKeys.map((key) => {
		const i = apiLabels.findIndex((l) => String(l).trim() === String(key).trim());
		return i >= 0 ? apiValues[i] : 0;
	});
}

/** Build date labels (display) and date keys (yyyy-MM-dd) for the selected range. */
function buildDateRangeLabels(startDate, endDate) {
	const labels = [];
	const dateKeys = [];
	let current = new Date(startDate);
	const end = new Date(endDate);
	while (isBefore(current, end) || isEqual(current, end)) {
		dateKeys.push(format(current, "yyyy-MM-dd"));
		labels.push(format(current, "MMM dd"));
		current = addDays(current, 1);
	}
	if (labels.length === 0) {
		dateKeys.push(format(new Date(startDate), "yyyy-MM-dd"));
		labels.push(format(new Date(startDate), "MMM dd"));
	}
	return { labels, dateKeys };
}

const conversionOptions = {
	responsive: true,
	plugins: {
		legend: {
			display: true,
			position: "top",
		},
	},
	scales: {
		x: {
			grid: { display: false },
		},
		y: {
			beginAtZero: true,
		},
	},
};

const ProgressBar = ({ progress, label, isActive }) => {
	const progressColor = isActive ? "#5c50e6" : "#999";
	const textColor = isActive ? "#333" : "#999";
	const filter = isActive ? "none" : "grayscale(100%)";
	
	return (
		<div style={{ marginBottom: '20px', filter }}>
			<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
				<span style={{ fontWeight: 600, color: textColor }}>{label}</span>
				<span style={{ color: textColor }}>{progress}%</span>
			</div>
			<div
				style={{
					width: "100%",
					background: "#DDD",
					borderRadius: 6,
					overflow: "hidden",
					height: 14,
				}}>
				<div
					style={{
						width: `${progress}%`,
						background: progressColor,
						height: "100%",
						transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
					}}
				/>
			</div>
		</div>
	);
};

const LINK_RECORDS_PAGE_SIZE = 20;

const Dashboard = ({ embedded = false }) => {
	const { currentWebsiteId, dateRange, setDateRange } = useContext(ProductContext);
	const [pageLoader, setPageLoader] = useState(true);
	const intervalRef = useRef(null);
	const [onboardingStatus, setOnboardingStatus] = useState({
		contentSync: false,
		contentProcess: false,
		aiTraining: false,
		onboarded: false,
		contentSyncProgress: 0,
		contentProcessProgress: 0,
		aiTrainingProgress: 0,
	});
	const [chatStats, setChatStats] = useState(null);
	const [searchStats, setSearchStats] = useState(null);
	const [linkRecords, setLinkRecords] = useState([]);
	const [linkRecordsTotal, setLinkRecordsTotal] = useState(0);
	const [linkRecordsPage, setLinkRecordsPage] = useState(0);
	const [chatStatsLoader, setChatStatsLoader] = useState(false);
	const [searchStatsLoader, setSearchStatsLoader] = useState(false);
	const [linkRecordsLoader, setLinkRecordsLoader] = useState(false);
	const [analyticsTab, setAnalyticsTab] = useState("widget"); // widget | search

	// Get websiteId from context or cookies
	const websiteId = currentWebsiteId || Cookies.get("_website_id");

	// Steps configuration
	const steps = [
		{
			label: "Content Sync",
			description: "Syncing content from CMS to Elasticsearch",
		},
		{
			label: "Content Process",
			description: "Processing content and uploading to RAGFlow",
		},
		{
			label: "AI Training",
			description: "Training your AI assistant",
		},
	];

	// Fetch onboarding status
	useEffect(() => {
		if (!websiteId) {
			setPageLoader(false);
			return;
		}

		const fetchOnboardingStatus = async () => {
			try {
				const response = await PostData("ms4", "api/v1/cmsAssistant/getOnboardingStatus", {
					websiteId: websiteId,
				});

				if (response !== "Invalid" && response !== undefined) {
					if (response.status === "success" && response.data) {
						setOnboardingStatus({
							contentSync: response.data.contentSync || false,
							contentProcess: response.data.contentProcess || false,
							aiTraining: response.data.aiTraining || false,
							onboarded: response.data.onboarded || false,
							contentSyncProgress: response.data.contentSyncProgress || 0,
							contentProcessProgress: response.data.contentProcessProgress || 0,
							aiTrainingProgress: response.data.aiTrainingProgress || 0,
						});
						if( intervalRef.current && response.data.aiTraining ){
							clearInterval(intervalRef.current);
						}
					}
				}
			} catch (error) {
				console.error("Error fetching onboarding status:", error);
				toast.error("Failed to fetch onboarding status");
			} finally {
				setPageLoader(false);
			}
		};

		fetchOnboardingStatus();

		// Poll API every 10 seconds for updates
		intervalRef.current = setInterval(() => {
			fetchOnboardingStatus();
		}, 10000);

		return () => {
			clearInterval(intervalRef.current);
		};
	}, [websiteId]);

	// Fetch chat stats when onboarded (unique users, conversations, contact submissions); uses dateRange from context
	useEffect(() => {
		if (!websiteId || !onboardingStatus.onboarded || !dateRange?.startDate || !dateRange?.endDate) {
			setChatStats(null);
			return;
		}
		const startDate = format(new Date(dateRange.startDate), "yyyy-MM-dd");
		const endDate = format(new Date(dateRange.endDate), "yyyy-MM-dd");

		const fetchChatStats = async () => {
			setChatStatsLoader(true);
			try {
				const chatResponse = await PostData("ms4", "api/v1/cmsAssistant/getChatStats", {
					websiteId,
					startDate,
					endDate,
				});
				if (chatResponse?.status === "success" && chatResponse?.data) {
					setChatStats(chatResponse.data);
				}
			} catch (error) {
				console.error("Error fetching chat stats:", error);
				toast.error("Failed to load dashboard stats");
			} finally {
				setChatStatsLoader(false);
			}
		};

		fetchChatStats();
	}, [websiteId, onboardingStatus.onboarded, dateRange?.startDate, dateRange?.endDate]);

	// Fetch Search + AI Overview stats when Search tab is active
	useEffect(() => {
		if (
			!websiteId ||
			!onboardingStatus.onboarded ||
			!dateRange?.startDate ||
			!dateRange?.endDate ||
			analyticsTab !== "search"
		) {
			return;
		}
		const startDate = format(new Date(dateRange.startDate), "yyyy-MM-dd");
		const endDate = format(new Date(dateRange.endDate), "yyyy-MM-dd");

		const fetchSearchStats = async () => {
			setSearchStatsLoader(true);
			try {
				const response = await PostData("ms4", "api/v1/cmsAssistant/getSearchStats", {
					websiteId,
					startDate,
					endDate,
				});
				if (response?.status === "success" && response?.data) {
					setSearchStats(response.data);
				} else {
					setSearchStats(null);
				}
			} catch (error) {
				console.error("Error fetching search stats:", error);
				toast.error("Failed to load search analytics");
			} finally {
				setSearchStatsLoader(false);
			}
		};

		fetchSearchStats();
	}, [websiteId, onboardingStatus.onboarded, dateRange?.startDate, dateRange?.endDate, analyticsTab]);

	// Fetch paginated link tracking records for table
	useEffect(() => {
		if (!websiteId || !onboardingStatus.onboarded || !dateRange?.startDate || !dateRange?.endDate) {
			setLinkRecords([]);
			setLinkRecordsTotal(0);
			return;
		}
		const startDate = format(new Date(dateRange.startDate), "yyyy-MM-dd");
		const endDate = format(new Date(dateRange.endDate), "yyyy-MM-dd");

		const fetchLinkRecords = async () => {
			setLinkRecordsLoader(true);
			try {
				const response = await PostData("ms4", "api/v1/cmsAssistant/getLinkTrackingRecords", {
					websiteId,
					startDate,
					endDate,
					page: linkRecordsPage + 1,
					limit: LINK_RECORDS_PAGE_SIZE,
				});
				if (response?.status === "success" && response?.data) {
					setLinkRecords(response.data.records || []);
					setLinkRecordsTotal(response.data.pagination?.total || 0);
				}
			} catch (error) {
				console.error("Error fetching link tracking records:", error);
				toast.error("Failed to load link tracking records");
			} finally {
				setLinkRecordsLoader(false);
			}
		};

		fetchLinkRecords();
	}, [websiteId, onboardingStatus.onboarded, dateRange?.startDate, dateRange?.endDate, linkRecordsPage]);

	useEffect(() => {
		setLinkRecordsPage(0);
	}, [dateRange?.startDate, dateRange?.endDate, websiteId]);

	// Determine current active step
	const getCurrentStep = () => {
		if (!onboardingStatus.contentSync) return 0;
		if (!onboardingStatus.contentProcess) return 1;
		if (!onboardingStatus.aiTraining) return 2;
		return 3; // All complete
	};

	const currentStep = getCurrentStep();
	const linkRecordsPageCount = Math.ceil(linkRecordsTotal / LINK_RECORDS_PAGE_SIZE) || 0;

	const handleLinkRecordsPageClick = (event) => {
		setLinkRecordsPage(event.selected);
	};

	if (pageLoader && !embedded) {
		return <DetailSkeleton />;
	}

	return (
		<div className={embedded ? "cms-assistant-dashboard" : "pageContent cms-assistant-dashboard"}>
			{!embedded && (
				<Row className="mt-3 align-items-center justify-content-between">
					<Col xs="auto">
						<h2>Dashboard</h2>
					</Col>
					{onboardingStatus.onboarded && (
						<Col xs="auto">
							<DateRangePickerComponent
								dateRange={dateRange}
								onSelect={(range) => setDateRange(range)}
							/>
						</Col>
					)}
				</Row>
			)}

			<Card className={embedded ? "mt-3" : "mt-4"}>
				{embedded && pageLoader && (
					<CardBody>
						<CredentialSkeleton height={120} />
					</CardBody>
				)}
				{!(embedded && pageLoader) && (
				<CardBody>
					{onboardingStatus.onboarded ? (
						<>
							<div className="row mt-3">
								<Col xs="12" className="d-flex align-items-center gap-2 flex-wrap">
									<button
										type="button"
										className={`btn btn-sm ${analyticsTab === "widget" ? "btn-primary" : "btn-outline-secondary"}`}
										onClick={() => setAnalyticsTab("widget")}
									>
										Widget
									</button>
									<button
										type="button"
										className={`btn btn-sm ${analyticsTab === "search" ? "btn-primary" : "btn-outline-secondary"}`}
										onClick={() => setAnalyticsTab("search")}
									>
										Search
									</button>
								</Col>
							</div>

							{analyticsTab === "widget" ? (
							<>
							{/* Widget analytics section - match Search Dashboard layout */}
							<div className="row mt-3">
								<Col xs="12">
									<h3>Widget analytics</h3>
								</Col>
							</div>
							<Row className="mt-3 justify-content-between">
								<div className="row mt-3">
									<div style={{ display: "flex", flexDirection: "row", justifyContent: "start", gap: "16px", flexWrap: "wrap", width: "100%" }}>
										<div className="boxOne search" style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", borderTopColor: "#2b4cc5" }}>
											<div className="top-row">
												<span>Unique users</span>
												<i className="fa fa-users" aria-hidden="true" style={{ marginLeft: "8px" }} />
											</div>
											{chatStatsLoader ? (
												<div><Spinner style={{ width: "20px", height: "20px" }} />{" "}</div>
											) : (
												<b>{chatStats?.uniqueUsers ?? 0}</b>
											)}
										</div>
										<div className="boxOne click" style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", borderTopColor: "#d88d49" }}>
											<div className="top-row">
												<span>Conversations</span>
												<i className="fa fa-comments" aria-hidden="true" style={{ marginLeft: "8px" }} />
											</div>
											{chatStatsLoader ? (
												<div><Spinner style={{ width: "20px", height: "20px" }} />{" "}</div>
											) : (
												<b>{chatStats?.totalConversations ?? 0}</b>
											)}
										</div>
										<div className="boxTwo price" style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", borderTopColor: "#008000" }}>
											<div className="top-row">
												<span>Contact forms submitted</span>
												<i className="fa fa-envelope" aria-hidden="true" style={{ marginLeft: "8px" }} />
											</div>
											{chatStatsLoader ? (
												<div><Spinner style={{ width: "20px", height: "20px" }} />{" "}</div>
											) : (
												<b>{chatStats?.totalContactSubmissions ?? 0}</b>
											)}
										</div>
									</div>
								</div>
							</Row>
							{/* Date-wise graph */}
							<div className="row mt-5">
								{chatStatsLoader ? (
									<CredentialSkeleton height={40} />
								) : chatStats ? (
									<Line
										data={(() => {
											const start = new Date(dateRange?.startDate || subDays(new Date(), 30));
											const end = new Date(dateRange?.endDate || new Date());
											const { labels, dateKeys } = buildDateRangeLabels(start, end);
											const daily = chatStats?.dailyStats || {};
											return {
												labels,
												datasets: [
													{ label: "Unique users", data: mapDailyToDateRange(dateKeys, daily.uniqueUsers || []), fill: false, borderColor: "#2b4cc5", tension: 0.3 },
													{ label: "Conversations", data: mapDailyToDateRange(dateKeys, daily.conversations || []), fill: false, borderColor: "#d88d49", tension: 0.3 },
													{ label: "Contact submissions", data: mapDailyToDateRange(dateKeys, daily.contactSubmissions || []), fill: false, borderColor: "#008000", tension: 0.3 },
												],
											};
										})()}
										options={conversionOptions}
									/>
								) : null}
							</div>
							{!chatStatsLoader && chatStats?.topRetrievalTitles?.length > 0 ? (
								<div className="row mt-5">
									<Col xs="12">
										<h4>Top retrieved sources</h4>
										<p className="text-muted small mb-3">
											How often each knowledge-base title appeared in RAG retrieval for assistant replies in the selected date range (new chats only).
										</p>
										<div className="table-responsive" style={{ maxWidth: 720 }}>
											<table className="table table-sm table-bordered mb-0">
												<thead className="table-light">
													<tr>
														<th scope="col">Source title</th>
														<th scope="col" className="text-end" style={{ width: 100 }}>
															Count
														</th>
													</tr>
												</thead>
												<tbody>
													{chatStats.topRetrievalTitles.map((row, i) => (
														<tr key={`${row.title}-${i}`}>
															<td>{row.title}</td>
															<td className="text-end">{row.count}</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</Col>
								</div>
							) : null}
							</>
							) : (
							<>
							<div className="row mt-3">
								<Col xs="12">
									<h3>Search analytics</h3>
									<p className="text-muted small mb-0">
										Summary KPIs for results-page search and AI Overview. Open Reports in the sidebar for full tables, sort, pagination, and export.
									</p>
								</Col>
							</div>
							<Row className="mt-3">
								<Col xs="12">
									<div className="search-analytics-kpi-grid">
										<div className="search-kpi-card" style={{ borderTopColor: "#2b4cc5" }}>
											<p
												className="search-kpi-label"
												title="Searches on the AI search results page (with session context)."
											>
												<span>Searches</span>
												<i className="fa fa-search" aria-hidden="true" />
											</p>
											{searchStatsLoader ? (
												<Spinner style={{ width: "20px", height: "20px" }} />
											) : (
												<div className="search-kpi-value">
													{searchStats?.totalSearchesResultsPage ?? searchStats?.totalSearches ?? 0}
												</div>
											)}
										</div>
										<div className="search-kpi-card" style={{ borderTopColor: "#d88d49" }}>
											<p className="search-kpi-label">
												<span>Unique searchers</span>
												<i className="fa fa-users" aria-hidden="true" />
											</p>
											{searchStatsLoader ? (
												<Spinner style={{ width: "20px", height: "20px" }} />
											) : (
												<div className="search-kpi-value">{searchStats?.uniqueSearchers ?? 0}</div>
											)}
										</div>
										<div className="search-kpi-card" style={{ borderTopColor: "#008000" }}>
											<p className="search-kpi-label">
												<span>AI Overviews</span>
												<i className="fa fa-robot" aria-hidden="true" />
											</p>
											{searchStatsLoader ? (
												<Spinner style={{ width: "20px", height: "20px" }} />
											) : (
												<div className="search-kpi-value">{searchStats?.totalAiOverviews ?? 0}</div>
											)}
										</div>
										<div className="search-kpi-card" style={{ borderTopColor: "#9c27b0" }}>
											<p
												className="search-kpi-label"
												title="Share of searches where the keyword result list was empty."
											>
												<span>Zero keyword results</span>
												<i className="fa fa-exclamation-circle" aria-hidden="true" />
											</p>
											{searchStatsLoader ? (
												<Spinner style={{ width: "20px", height: "20px" }} />
											) : (
												<div className="search-kpi-value">{searchStats?.zeroResultRate ?? 0}%</div>
											)}
										</div>
										<div className="search-kpi-card" style={{ borderTopColor: "#e91e63" }}>
											<p className="search-kpi-label">
												<span>Result CTR</span>
												<i className="fa fa-mouse-pointer" aria-hidden="true" />
											</p>
											{searchStatsLoader ? (
												<Spinner style={{ width: "20px", height: "20px" }} />
											) : (
												<div className="search-kpi-value">{searchStats?.resultCtr ?? 0}%</div>
											)}
										</div>
									</div>
								</Col>
							</Row>
							<div className="row mt-5">
								{searchStatsLoader ? (
									<CredentialSkeleton height={40} />
								) : searchStats ? (
									<Line
										data={(() => {
											const start = new Date(dateRange?.startDate || subDays(new Date(), 30));
											const end = new Date(dateRange?.endDate || new Date());
											const { labels, dateKeys } = buildDateRangeLabels(start, end);
											const daily = searchStats?.dailyStats || {};
											return {
												labels,
												datasets: [
													{ label: "Searches", data: mapDailyToDateRange(dateKeys, daily.searches || []), fill: false, borderColor: "#2b4cc5", tension: 0.3 },
													{ label: "AI Overviews", data: mapDailyToDateRange(dateKeys, daily.aiOverviews || []), fill: false, borderColor: "#008000", tension: 0.3 },
												],
											};
										})()}
										options={conversionOptions}
									/>
								) : null}
							</div>
							{!searchStatsLoader && searchStats ? (
								<div className="row mt-5">
									<Col md="6" className="mb-4">
										<div className="d-flex justify-content-between align-items-baseline">
											<h4 className="mb-0">Top queries</h4>
											<Link to="/cmsassistant/reports/searchTerms" className="small">View full report →</Link>
										</div>
										<div className="table-responsive">
											<table className="table table-sm table-bordered mb-0">
												<thead className="table-light">
													<tr>
														<th scope="col">Query</th>
														<th scope="col" className="text-end" style={{ width: 100 }}>Count</th>
													</tr>
												</thead>
												<tbody>
													{(searchStats.topQueries || []).length ? (
														searchStats.topQueries.map((row, i) => (
															<tr key={`tq-${i}`}>
																<td>{row.query}</td>
																<td className="text-end">{row.count}</td>
															</tr>
														))
													) : (
														<tr><td colSpan="2" className="text-center text-muted">No data</td></tr>
													)}
												</tbody>
											</table>
										</div>
									</Col>
									<Col md="6" className="mb-4">
										<div className="d-flex justify-content-between align-items-baseline">
											<h4 className="mb-0">Zero keyword results</h4>
											<Link to="/cmsassistant/reports/zeroKeywordResults" className="small">View full report →</Link>
										</div>
										<div className="table-responsive">
											<table className="table table-sm table-bordered mb-0">
												<thead className="table-light">
													<tr>
														<th scope="col">Query</th>
														<th scope="col" className="text-end" style={{ width: 72 }}>Zero</th>
														<th scope="col" className="text-end" style={{ width: 72 }}>w/ AI</th>
													</tr>
												</thead>
												<tbody>
													{(searchStats.zeroResultQueries || []).length ? (
														searchStats.zeroResultQueries.map((row, i) => (
															<tr key={`zq-${i}`}>
																<td>{row.query}</td>
																<td className="text-end">{row.count}</td>
																<td className="text-end">{row.withAiOverviewCount ?? 0}</td>
															</tr>
														))
													) : (
														<tr><td colSpan="3" className="text-center text-muted">No data</td></tr>
													)}
												</tbody>
											</table>
										</div>
									</Col>
									<Col md="6" className="mb-4">
										<div className="d-flex justify-content-between align-items-baseline">
											<h4 className="mb-0">Top clicked results</h4>
											<Link to="/cmsassistant/reports/resultsClicked" className="small">View full report →</Link>
										</div>
										<div className="table-responsive">
											<table className="table table-sm table-bordered mb-0">
												<thead className="table-light">
													<tr>
														<th scope="col">Result</th>
														<th scope="col" className="text-end" style={{ width: 100 }}>Clicks</th>
													</tr>
												</thead>
												<tbody>
													{(searchStats.topClickedResults || []).length ? (
														searchStats.topClickedResults.map((row, i) => (
															<tr key={`cr-${i}`}>
																<td>
																	<div>{row.title || "—"}</div>
																	{row.url ? (
																		<a href={row.url} target="_blank" rel="noopener noreferrer" className="small">
																			{formatLinkLabel(row.url)}
																		</a>
																	) : null}
																</td>
																<td className="text-end">{row.count}</td>
															</tr>
														))
													) : (
														<tr><td colSpan="2" className="text-center text-muted">No data</td></tr>
													)}
												</tbody>
											</table>
										</div>
									</Col>
									<Col md="6" className="mb-4">
										<div className="d-flex justify-content-between align-items-baseline">
											<h4 className="mb-0">Top AI Overview citations</h4>
											<Link to="/cmsassistant/reports/topAiCitations" className="small">View full report →</Link>
										</div>
										<div className="table-responsive">
											<table className="table table-sm table-bordered mb-0">
												<thead className="table-light">
													<tr>
														<th scope="col">Citation</th>
														<th scope="col" className="text-end" style={{ width: 100 }}>Count</th>
													</tr>
												</thead>
												<tbody>
													{(searchStats.topCitations || []).length ? (
														searchStats.topCitations.map((row, i) => (
															<tr key={`ct-${i}`}>
																<td>{row.title}</td>
																<td className="text-end">{row.count}</td>
															</tr>
														))
													) : (
														<tr><td colSpan="2" className="text-center text-muted">No data</td></tr>
													)}
												</tbody>
											</table>
										</div>
									</Col>
								</div>
							) : null}
							</>
							)}
						</>
					) : (
						<>
							<Alert color="info" className="mb-4">
								<h5 className="mb-2">Setting up your CMS Assistant</h5>
								<p className="mb-0">
									{currentStep < steps.length
										? steps[currentStep].description
										: "We will notify you after setting up your account."}
								</p>
							</Alert>
							<div style={{ maxWidth: '600px', margin: '0 auto' }}>
							<ProgressBar
								progress={onboardingStatus.contentSyncProgress}
								label={steps[0].label}
								isActive={currentStep === 0 || onboardingStatus.contentSync}
							/>

							<ProgressBar
								progress={onboardingStatus.contentProcessProgress}
								label={steps[1].label}
								isActive={currentStep === 1 || onboardingStatus.contentProcess}
							/>

							<ProgressBar
								progress={onboardingStatus.aiTrainingProgress}
								label={steps[2].label}
								isActive={currentStep === 2 || onboardingStatus.aiTraining}
							/>
						</div>
					</>
					)}
				</CardBody>
				)}
			</Card>

			{onboardingStatus.onboarded && analyticsTab === "widget" && !(embedded && pageLoader) && (
				<Card className={embedded ? "mt-3" : "mt-4"}>
					<CardBody>
						<div className="row">
							<Col xs="12">
								<h3>Message link activity</h3>
							</Col>
						</div>
						<p className="text-muted small mb-4">
							Links clicked in chat messages and attributed page visits (utm_source=chat) in the selected date range.
						</p>
						<div className="table-responsive cms-link-activity-table-wrap">
							<table className="cms-link-activity-table">
								<thead>
									<tr>
										<th scope="col">Page title</th>
										<th scope="col">Link</th>
										<th scope="col" className="text-end" style={{ width: 100 }}>Clicks</th>
										<th scope="col" className="text-end" style={{ width: 120 }}>Page visits</th>
									</tr>
								</thead>
								<tbody>
									{linkRecordsLoader ? (
										<tr>
											<td colSpan="4" className="text-center cms-link-activity-empty">
												<Spinner style={{ width: "20px", height: "20px" }} />
											</td>
										</tr>
									) : linkRecords.length > 0 ? (
										linkRecords.map((row, i) => (
											<tr key={`${row.link}-${i}`}>
												<td>
													<div className="cms-link-activity-page-title">
														{row.pageTitle || "—"}
													</div>
												</td>
												<td>
													<a
														href={row.link}
														target="_blank"
														rel="noopener noreferrer"
														className="cms-link-activity-url"
														title={row.link}
													>
														{formatLinkLabel(row.link)}
													</a>
												</td>
												<td className="text-end cms-link-activity-metric">{row.clicks ?? 0}</td>
												<td className="text-end cms-link-activity-metric">{row.pageViews ?? 0}</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="4" className="text-center cms-link-activity-empty">
												No link activity in this date range
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
						{linkRecordsPageCount > 1 && (
							<div className="d-flex justify-content-center mt-4">
								<ReactPaginate
									previousLabel={<FontAwesomeIcon icon={faChevronLeft} />}
									nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
									breakLabel={"..."}
									breakClassName={"break-me"}
									pageCount={linkRecordsPageCount}
									marginPagesDisplayed={2}
									pageRangeDisplayed={5}
									onPageChange={handleLinkRecordsPageClick}
									forcePage={linkRecordsPage}
									containerClassName={"pagination"}
									activeClassName={"active"}
								/>
							</div>
						)}
					</CardBody>
				</Card>
			)}
		</div>
	);
};

export default Dashboard;
